import React, { FC, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Typography, Select, MenuItem, InputLabel } from '@material-ui/core';

import { listChannels } from './apiQueries';
import { sortAlphabeticalOrder } from '../../utils/ordering';
import { Channel } from '../../utils/types';

/**
 * ChannelListDropdown component renders a dropdown menu for selecting a channel.
 * It fetches the list of channels using a GraphQL query and sorts them alphabetically.
 * If a default channel name is provided, it selects that channel by default.
 *
 * @component
 * @param {ChannelListDropdownProps} props - The properties passed to the component.
 * @param {string} props.channelId - The currently selected channel ID.
 * @param {string} props.defaultChannelName - The name of the default channel to be selected.
 * @param {boolean} props.hideLabel - Flag to hide the input label.
 * @param {function} props.onSelectChange - Callback function to handle the change event when a channel is selected.
 *
 * @returns {JSX.Element} The rendered ChannelListDropdown component.
 */
const ChannelListDropdown: FC<ChannelListDropdownProps> = (props) => {
	const { channelId, defaultChannelName, hideLabel, onSelectChange } = props;

	const { loading, error, data } = useQuery(listChannels, {
		variables: { nextToken: null, sortDirection: 'DESC' }
	});

	const orderedChannels: Channel[] = data?.listChannels.items.sort((a: Channel, b: Channel): number => sortAlphabeticalOrder(a.id, b.id));
	const defaultChannelId = orderedChannels?.find((channel: Channel): boolean => channel.name === defaultChannelName)?.id;

	useEffect(() => {
		if (defaultChannelId && !channelId) onSelectChange({ target: { value: defaultChannelId } });
	}, [channelId, defaultChannelId, onSelectChange]);

	const inputLabel = !hideLabel && <InputLabel id='channel-label'>Channel ID</InputLabel>;

	const emptyListState = (message: string): JSX.Element => {
		return (
			<>
				{inputLabel}
				<Typography>{message}</Typography>
			</>
		);
	};

	const renderChannelMenu = (channelList: Channel[]): JSX.Element[] => {
		const channelsByType: { [key: string]: { index: number; channelId: string }[] } = {};

		channelList.forEach((channel: Channel, index: number) => {
			const channelTypeValue = channel.type ?? 'Others';
			const valueList = channelsByType[channelTypeValue];
			if (!valueList) channelsByType[channelTypeValue] = [];
			channelsByType[channelTypeValue].push({ index, channelId: channel.id });
		});

		let menuList: JSX.Element[] = [];
		Object.entries(channelsByType).forEach(([key, value]) => {
			menuList = [
				<MenuItem disabled value=''>
					<em>{key}</em>
				</MenuItem>,
				...value.map((channelDetail) => (
					<MenuItem key={channelDetail.index} value={channelDetail.channelId}>
						{channelDetail.channelId}
					</MenuItem>
				)),
				...menuList
			];
		});

		return menuList;
	};

	if (loading) return emptyListState('Loading...');
	if (error) return emptyListState('Error loading data from the server');
	if (!data.listChannels) return emptyListState('No channels found');

	return (
		<>
			{inputLabel}
			<Select
				id='channel-select'
				name='channelId'
				labelId='channel-label'
				value={channelId || defaultChannelId || ''}
				onChange={onSelectChange}
				fullWidth>
				{renderChannelMenu(orderedChannels)}
			</Select>
		</>
	);
};

type ChannelListDropdownProps = {
	channelId: string | null;
	defaultChannelName?: string;
	hideLabel?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSelectChange: (event: any) => void;
};

export default ChannelListDropdown;
